import React, { useLayoutEffect, useRef } from 'react';
import { Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';

export default function NavigationMemory({ children }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const { filters, setFilters } = useAppContext();
  const visits = useRef(new Map());
  const contexts = useRef(new Map());
  const scroll = useRef(new Map());
  const isTeamDetail = location.pathname === '/estadisticasEquipos' && new URLSearchParams(location.search).has('id');
  const viewPath = `${location.pathname}${isTeamDetail ? '#detail' : ''}`;
  const previousPath = useRef(viewPath);
  const previousEntry = useRef(null);
  const samePage = previousPath.current === viewPath;
  const scope = [filters.temporada, filters.competicion, filters.grupo].join(':');
  const key = `${location.pathname}:${scope}`;
  const remembered = visits.current.get(key);
  const savedContext = contexts.current.get(location.key);
  const restoreContext = navigationType === 'POP' && previousEntry.current !== location.key &&
    savedContext && savedContext.scope !== scope;
  const restore = ['/partidos', '/clasificacion', '/clubes', '/estadisticasEquipos', '/rankingJugadores', '/estadisticasArbitros'].includes(location.pathname) &&
    previousPath.current !== viewPath && navigationType === 'PUSH' &&
    !location.search && remembered;

  useLayoutEffect(() => {
    const positions = scroll.current;
    if (restoreContext) {
      setFilters(savedContext.filters);
      return undefined;
    }
    if (restore) return undefined;
    previousPath.current = viewPath;
    contexts.current.set(location.key, { scope, filters });
    if (!isTeamDetail) visits.current.set(key, location.search);
    const root = document.getElementById('root');
    const target = root && ['auto', 'scroll'].includes(getComputedStyle(root).overflowY) ? root : window;
    if (previousEntry.current !== location.key) {
      const currentPosition = target === window ? window.scrollY : target.scrollTop;
      const position = navigationType === 'POP' ? positions.get(location.key) || 0
        : navigationType === 'REPLACE' && samePage ? currentPosition : 0;
      if (target === window) window.scrollTo(0, position);
      else target.scrollTop = position;
    }
    previousEntry.current = location.key;
    const rememberScroll = () => positions.set(location.key, target === window ? window.scrollY : target.scrollTop);
    rememberScroll();
    target.addEventListener('scroll', rememberScroll, { passive: true });
    return () => target.removeEventListener('scroll', rememberScroll);
  }, [filters, isTeamDetail, key, location.key, location.pathname, location.search, navigationType, restore, restoreContext, samePage, savedContext, scope, setFilters, viewPath]);

  if (restoreContext) return null;
  return restore ? <Navigate to={{ pathname: location.pathname, search: remembered }} replace /> : children;
}
